import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

interface C1ChartCardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  /** Trailing control on the title row, e.g. a chart/table toggle. */
  action?: ReactNode;
  /** Note under the content — scope, threshold, or a truncation warning. */
  caption?: ReactNode;
  children: ReactNode;
  /** Let the card span both columns of the charts grid. */
  wide?: boolean;
}

/**
 * The surface every chart sits on: a figure with a captioned title row.
 *
 * A `<figure>` with a `<figcaption>` rather than a div with a heading — the
 * title belongs to the graphic, and this keeps them associated for a screen
 * reader without inventing a heading level in the middle of a page.
 */
export function C1ChartCard({
  title,
  action,
  caption,
  children,
  wide = false,
  className,
  ...props
}: C1ChartCardProps) {
  return (
    <figure className={cx('c1-chartCard', wide && 'c1-chartCard--wide', className)} {...props}>
      <figcaption className="c1-chartCardTitle">
        <span>{title}</span>
        {action}
      </figcaption>
      <div className="c1-chartCardBody">{children}</div>
      {caption ? <p className="c1-chartCardCaption">{caption}</p> : null}
    </figure>
  );
}
